import { ActivatedRouteSnapshot, Router, Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { Injector, inject } from '@angular/core';
import { Entities, Permissions, PermissionsService } from './services/permissions.service';
import { LoggingService } from './services/logging.service';
import { Observable, from, map, of, switchMap } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';
import { MessageService } from './services/message.service';
import { UtilitiesService } from './services/utilities.service';
import { AmplifyService } from './services/amplify.service';

export const APP_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: '/summary' },
  {
    path: 'summary',
    component: HomeComponent,
    canActivate: [(route: ActivatedRouteSnapshot): Observable<boolean> => canActivate(route, Entities.Summary)],
  },
  {
    path: 'user',
    loadChildren: () => import('./pages/user/user.routes').then((r) => r.USER_ROUTES),
  },
  {
    path: 'investment-projects',
    loadChildren: () =>
      import('./pages/investment-projects/investment-projects.routes').then((r) => r.INVESTMENT_PROJECTS_ROUTES),
  },
  {
    path: 'notifications',
    loadChildren: () => import('./pages/notifications/notifications.routes').then((r) => r.NOTIFICATIONS_ROUTES),
  },
  { path: '**', redirectTo: '/summary', pathMatch: 'full' },
];

export function canActivate(route: ActivatedRouteSnapshot, entity: string): Observable<boolean> {
  const permissionsService = inject(PermissionsService);
  const logger = inject(LoggingService);
  const router = inject(Router);
  const injector = inject(Injector);
  const utilitiesService = inject(UtilitiesService);
  const amplifyService = inject(AmplifyService);

  if (!amplifyService.isSignedIn()) {
    return of(true);
  }

  utilitiesService.setCurrentRoute(entity);

  return from(permissionsService.loadPermissions()).pipe(
    switchMap(() => toObservable(permissionsService.can(Permissions.Read, entity), { injector: injector })),
    map((userCan) => {
      if (!userCan) {
        logger.error({
          message: $localize`Not authorized to view this page: ${route.url}`,
          error: new Error('Not authorized to view this page: ' + route.url),
        });
        router.navigate(['/summary']);
      }

      return userCan;
    }),
  );
}

export interface CanComponentDeactivate {
  canDeactivate: () => boolean | Observable<boolean>;
}

export function canDeactivate(component: CanComponentDeactivate): Observable<boolean> {
  const modal = inject(MessageService);

  if (component?.canDeactivate()) {
    return of(true);
  }

  const title = $localize`WARNING!`;
  const message = $localize`You have unsaved changes. Press "Continue editing" to stay on this page or "Quit the page" to exit the page and lose your changes.`;

  modal.confirm({
    title: title,
    content: message,
    okText: $localize`Quit the page`,
    cancelText: $localize`Continue editing`,
  });

  return modal.confirmAfterClose$.pipe(
    map((result) => {
      return result === true;
    }),
  );
}
